import { Box, Divider, List, ListItem, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import { getUserEmail } from '../../../helpers/authUser';
import { SubscriptionMonthlyLimit } from '../../organisms/subscriptions/SubscriptionSelector';

const useStyles = makeStyles((theme) => ({
    titlePlan: {
        fontFamily: 'Poppins',
        fontWeight: 'bold',
        fontSize: '2rem',
        color: theme.colors.primary[600],
    },
    featureList: {
        fontFamily: 'Inter',
        fontWeight: '600',
        fontSize: '1rem',
        color: '#666666',
    },
    featureCount: {
        fontFamily: 'Poppins',
        fontWeight: 'bold',
        fontSize: '1rem',
        color: theme.colors.primary[500],
    },
    discountChip: {
        height: 36,
        width: 96,
        borderRadius: 32,
        borderWidth: 3,
        padding: 8,
        fontSize: 18,
        fontWeight: 'bold',
        textTransform: 'none',
        background: theme.colors.neutral[50],
        color: theme.colors.cyan[400],
    },
    rateText: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: theme.colors.neutral[600],
    },
    discountedPrice: {
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: '1rem',
        color: theme.colors.warning[500],
        textAlign: 'right',
    },
    discountedPriceText: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        fontSize: '1.8rem',
        color: theme.colors.warning[500],
        textAlign: 'right',
    },
    originalPriceText: {
        position: 'relative',
        display: 'inline-block',
        fontFamily: 'Inter',
        fontWeight: 600,
        fontSize: '1.8rem',
        color: theme.colors.cyan[400],
    },
    originalPriceSlash: {
        position: 'relative',
        width: '100%',
        height: 0,
        top: -24,
        left: 8,
        borderTop: `2px solid ${theme.colors.cyan[400]}`,
        transform: 'rotate(-145deg)',
    },
    planButton: {
        width: 260,
        height: 50,
        borderRadius: 32,
        borderWidth: 3,
        textTransform: 'none',
        padding: '8px 24px',
        fontSize: '1.25rem',
        fontWeight: 'bold',
    },
    daysLeft: {
        marginTop: 8,
        marginBottom: -24,
        fontSize: '1rem',
        fontFamily: 'Inter',
        fontWeight: '500',
        color: theme.colors.warning[500],
    },
    url: {
        color: theme.colors.cyan[400],
        textDecoration: 'none',
        "&:hover": {
            textDecoration: 'none !important',
        }
    },
}));

const ProPlan: React.FC<{
    admin: string,
    daysLeft?: number,
    cancelled: boolean,
    hasPayment: boolean,
    loaded: boolean,
}> = ({ admin, daysLeft, cancelled, loaded  }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [isAdmin, setAdmin] = useState<boolean>(false);

    useEffect(() => {
        if (user && admin)
            setAdmin(admin === getUserEmail(user).toLowerCase());
    }, [admin, user]);

    return (<Box position="relative">
        <Stack direction="row" alignItems="center" justifyContent="space-between" p={2} height="80px">
            <Typography className={classes.titlePlan}>{'Pro'}</Typography>
            <Stack direction="column" alignItems="flex-end" width="100%">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" mb="-4px" width="100%">
                    {!!daysLeft ? (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                                <Typography className={classes.discountedPrice}>
                                    {'Trial discount'}
                                </Typography>
                                <Typography component="span" className={classes.discountedPriceText}>
                                    {'$50'}
                                </Typography>
                            </Stack>
                            <Typography component="div" className={classes.originalPriceText}>
                                {'$99'}
                                <Typography component="div" className={classes.originalPriceSlash} />
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography className={classes.originalPriceText}>{'$99'}</Typography>
                    )}
                </Stack>
                <Typography className={classes.rateText}>{'per month'}</Typography>
            </Stack>
        </Stack>
        <Divider />
        <Stack alignItems="flex-start" p={2} height="304px">
            <Typography fontFamily="Poppins" fontWeight="bold" fontSize="1.2rem">What's included:</Typography>
            {/* <Typography className={classes.featureList} mt={2}>Everything from the free plan plus:</Typography> */}
            <List sx={{ width: '100%', margin: '0 32px', listStyleType: 'disc' }}>
                <ListItem sx={{ py: '2px', paddingLeft: '0px !important', display: 'list-item' }}>
                    <Typography className={classes.featureList} display="inline"> Up to </Typography>
                    <Typography className={classes.featureCount} display="inline">
                        {`1 user`}
                    </Typography>
                </ListItem>
                <ListItem sx={{ py: '2px', paddingLeft: '0px !important', display: 'list-item' }}>
                    <Typography className={classes.featureCount} display="inline">
                        {`${SubscriptionMonthlyLimit.Pro_v2} companies`}
                    </Typography>
                    <Typography className={classes.featureList} display="inline"> per month </Typography>
                </ListItem>
                <ListItem sx={{ py: '2px', paddingLeft: 'unset', display: 'list-item' }}>
                    <Typography className={classes.featureList}>Connect to external tools</Typography>
                </ListItem>
                <ListItem sx={{ py: '2px', paddingLeft: 'unset', display: 'list-item' }}>
                    <Typography className={classes.featureList}>Add companies from emails and LinkedIn</Typography>
                </ListItem>
            </List>
        </Stack>
        <Divider />
        {loaded ? (
            <Stack alignItems="center" justifyContent="center" p={2} height="112px">
                {(!isAdmin) ? (<>
                    <Typography color="#666666" textAlign="center">
                        {'Contact your account administrator to manage your subscription '}
                        (<Typography
                            component="a"
                            className={classes.url}
                            href={'mailto:' + admin}
                            target="_blank"
                            rel="noopener noreferrer"
                            display="inline"
                        >{admin}</Typography>)
                    </Typography>
                </>) : (
                    <Typography color="#666666" textAlign="center">
                        {'To update your subscription, please reach us at '}
                        (<Typography
                            component="a"
                            className={classes.url}
                            href={'mailto:' + admin}
                            target="_blank"
                            rel="noopener noreferrer"
                            display="inline"
                        >{admin}</Typography>)
                    </Typography>
                )}
                {(!!daysLeft && !cancelled) && (
                    <Typography className={classes.daysLeft}>{`${daysLeft} day${daysLeft > 1 ? 's': ''} left for trial`}</Typography>
                )}
            </Stack>
        ) : (
            <Box height="112px" />
        )}
    </Box>);
}

export default ProPlan;